import {
  type ApiError,
  type ProgramCreate,
  type ProgramPublic,
  type ProgramUpdate,
  ProgramsService,
} from "@/client"
import { Field } from "@/components/ui/field"
import { toaster } from "@/components/ui/toaster"
import {
  Button,
  Input,
  NativeSelectField,
  NativeSelectRoot,
  Stack,
  Textarea,
  VStack,
} from "@chakra-ui/react"
import { useMutation, useQueryClient } from "@tanstack/react-query"
import { type SubmitHandler, useForm } from "react-hook-form"

interface ProgramFormProps {
  program?: ProgramPublic
  onSuccess?: () => void
}

interface ProgramFormData {
  name: string
  description?: string
  start_date?: string
  end_date?: string
  status: "active" | "inactive" | "archived"
}

export function ProgramForm({ program, onSuccess }: ProgramFormProps) {
  const queryClient = useQueryClient()
  const isEditing = !!program

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    reset,
  } = useForm<ProgramFormData>({
    defaultValues: {
      name: program?.name || "",
      description: program?.description || "",
      start_date: program?.start_date
        ? new Date(program.start_date).toISOString().split("T")[0]
        : "",
      end_date: program?.end_date
        ? new Date(program.end_date).toISOString().split("T")[0]
        : "",
      status: program?.status || "active",
    },
  })

  const createMutation = useMutation({
    mutationFn: (data: ProgramCreate) =>
      ProgramsService.createProgram({ requestBody: data }),
    onSuccess: () => {
      toaster.create({
        title: "Program created successfully",
        type: "success",
      })
      queryClient.invalidateQueries({ queryKey: ["programs"] })
      reset()
      onSuccess?.()
    },
    onError: (err: ApiError) => {
      toaster.create({
        title: "Failed to create program",
        description: err.message,
        type: "error",
      })
    },
  })

  const updateMutation = useMutation({
    mutationFn: (data: ProgramUpdate) =>
      ProgramsService.updateProgram({
        programId: program!.id,
        requestBody: data,
      }),
    onSuccess: () => {
      toaster.create({
        title: "Program updated successfully",
        type: "success",
      })
      queryClient.invalidateQueries({ queryKey: ["programs"] })
      queryClient.invalidateQueries({ queryKey: ["program", program!.id] })
      onSuccess?.()
    },
    onError: (err: ApiError) => {
      toaster.create({
        title: "Failed to update program",
        description: err.message,
        type: "error",
      })
    },
  })

  const onSubmit: SubmitHandler<ProgramFormData> = (data) => {
    const programData = {
      name: data.name,
      description: data.description || undefined,
      start_date: data.start_date
        ? new Date(data.start_date).toISOString()
        : undefined,
      end_date: data.end_date
        ? new Date(data.end_date).toISOString()
        : undefined,
      status: data.status,
    }

    if (isEditing) {
      updateMutation.mutate(programData)
    } else {
      createMutation.mutate(programData as ProgramCreate)
    }
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <VStack gap={4} align="stretch">
        <Field
          label="Program Name"
          required
          invalid={!!errors.name}
          errorText={errors.name?.message}
        >
          <Input
            {...register("name", {
              required: "Program name is required",
            })}
            placeholder="e.g., Computer Science 101"
          />
        </Field>

        <Field
          label="Description"
          invalid={!!errors.description}
          errorText={errors.description?.message}
        >
          <Textarea
            {...register("description")}
            placeholder="Program description"
            rows={3}
          />
        </Field>

        <Stack direction={{ base: "column", md: "row" }} gap={4}>
          <Field label="Start Date">
            <Input type="date" {...register("start_date")} />
          </Field>

          <Field label="End Date">
            <Input type="date" {...register("end_date")} />
          </Field>
        </Stack>

        <Field label="Status" required>
          <NativeSelectRoot>
            <NativeSelectField {...register("status")}>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
              <option value="archived">Archived</option>
            </NativeSelectField>
          </NativeSelectRoot>
        </Field>

        <Stack direction="row" justify="flex-end" gap={3}>
          {onSuccess && (
            <Button variant="outline" onClick={() => onSuccess()}>
              Cancel
            </Button>
          )}
          <Button type="submit" loading={isSubmitting} disabled={isSubmitting}>
            {isEditing ? "Update Program" : "Create Program"}
          </Button>
        </Stack>
      </VStack>
    </form>
  )
}
